/**
 * api-client.ts — Wrapper fetch.
 *
 * - Authorization (JWT session) — via cookie en prod, X-Dev-Bypass en dev
 * - L'app est mono-tenant (Gaz Systèmes), pas de routing par domaine
 *
 * Usage simple :
 *   const { items } = await api.get<{ items: Tender[] }>('/api/tenders');
 */

const BASE_URL = import.meta.env.VITE_API_URL ?? '';

/**
 * Token de session (JWT) stocké côté navigateur pour l'auth par en-tête
 * `Authorization: Bearer`. Indispensable quand le cookie de session est bloqué
 * comme cookie tiers (front pages.dev / API workers.dev). Le cookie reste posé
 * en parallèle pour les navigateurs qui l'acceptent (et pour iframe/SSE).
 */
const SESSION_TOKEN_KEY = 'tc_session_jwt';

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(SESSION_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setSessionToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(SESSION_TOKEN_KEY, token);
    else localStorage.removeItem(SESSION_TOKEN_KEY);
  } catch {
    /* localStorage indisponible (mode privé strict) : on ignore */
  }
}

/**
 * Build an absolute API URL for browser-driven navigations
 * (anchor `href`, `window.open`, etc.) where `api.get/post` cannot be used.
 *
 * In dev with proxy or same-origin deploy, `VITE_API_URL` is empty and the
 * returned path stays relative — the browser hits the same origin.
 *
 * In split-domain deploys (Cloudflare Pages + Workers on different domains),
 * `VITE_API_URL` points to the Worker URL and is prepended.
 */
export function apiHref(path: string): string {
  if (!BASE_URL) return path;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return `${BASE_URL}${path}`;
}

/**
 * Telecharge un fichier binaire depuis l'API et declenche le save dialog.
 *
 * Utilise pour les routes qui exigent auth (cookie OU X-Dev-Bypass header) :
 * un anchor `<a href>` ne peut pas porter ces headers cross-origin, donc on
 * passe par fetch + Blob + objectURL + click anchor temporaire.
 */
export async function downloadFile(path: string, fallbackFilename = 'download'): Promise<void> {
  const token = getSessionToken();
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (!res.ok) {
    let msg = `Download failed: ${res.status}`;
    try {
      const j = (await res.json()) as { message?: string };
      if (j.message) msg = j.message;
    } catch {
      // ignore
    }
    throw new ApiError(msg, res.status);
  }

  const cd = res.headers.get('content-disposition') ?? '';
  const m = cd.match(/filename="?([^"]+)"?/i);
  const filename = m?.[1] ?? fallbackFilename;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
  }
}

export type RequestOptions = {
  /** Headers additionnels. */
  headers?: Record<string, string>;
  /**
   * @deprecated Mono-tenant : option ignoree, conservee pour compat des
   * appels existants. Sera supprimee dans une passe ulterieure.
   */
  domainSlug?: string;
};

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };

  if (body && !(body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  const token = getSessionToken();
  if (token && !headers['Authorization']) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    credentials: 'include',
    body:
      body instanceof FormData
        ? body
        : body !== undefined
          ? JSON.stringify(body)
          : undefined,
  });

  if (!res.ok) {
    if (res.status === 401 && !window.location.pathname.startsWith('/login')) {
      setSessionToken(null); // token expiré/invalide → on le purge
      const next = encodeURIComponent(`${window.location.pathname}${window.location.search}`);
      window.location.href = `/login?next=${next}`;
    }
    let errBody: { message?: string; code?: string; details?: unknown } = {};
    try {
      errBody = await res.json();
    } catch {
      // ignore
    }
    throw new ApiError(
      errBody.message ?? `Request failed: ${res.status}`,
      res.status,
      errBody.code,
      errBody.details,
    );
  }

  if (res.status === 204) return undefined as T;
  const contentType = res.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) return res.json() as Promise<T>;
  return res.text() as Promise<T>;
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>('GET', path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('POST', path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PATCH', path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>('PUT', path, body, opts),
  delete: <T>(path: string, opts?: RequestOptions) => request<T>('DELETE', path, undefined, opts),
  upload: <T>(path: string, formData: FormData, opts?: RequestOptions) =>
    request<T>('POST', path, formData, opts),
};

export { ApiError };
